import { STORAGE_KEYS } from '@/utils/storageKeys';
import { useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { locationTracker } from '../services/locationTracker';

/**
 * Custom hook to track the previous location in the application
 * Uses sessionStorage to persist location across page refreshes
 * @returns {string | null} The previous location pathname
 */
export const usePreviousLocation = (): string | null => {
  const location = useLocation();
  const previousLocationRef = useRef<string | null>(null);

  useEffect(() => {
    const currentPath = location.pathname;
    const previousLocation = locationTracker.updateLocation(currentPath);

    if (previousLocation) {
      previousLocationRef.current = previousLocation;
    } else {
      // Fallback to sessionStorage if service doesn't have previous location
      const storedPreviousLocation = sessionStorage.getItem(
        STORAGE_KEYS.PREVIOUS_LOCATION_PATHNAME
      );
      previousLocationRef.current = storedPreviousLocation;
    }
  }, [location.pathname]);

  return previousLocationRef.current;
};

/**
 * Hook to initialize location tracking at the app level
 * Should be used in the main routing component (PrivateRoutes)
 */
export const useLocationTracker = () => {
  const location = useLocation();

  useEffect(() => {
    const currentPath = location.pathname;
    locationTracker.updateLocation(currentPath);
  }, [location.pathname]);
};

/**
 * Hook to check if user came from a specific path
 * @param path - The path to check against
 * @returns True if the previous location contains the specified path
 */
export const useCameFromPath = (path: string): boolean => {
  const location = useLocation();
  const [cameFromPath, setCameFromPath] = useState(false);

  useEffect(() => {
    const currentPath = location.pathname;
    locationTracker.updateLocation(currentPath);
    setCameFromPath(locationTracker.getPreviousLocation()?.includes(path));
  }, [location.pathname, path]);

  return cameFromPath;
};
